import React, { useState } from 'react';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CheckCircle, X } from 'lucide-react';
import { faqs, pricingPlans as mockPricingPlans } from '../data/mock';
import { useCurrency } from '../contexts/CurrencyContext';
import { getAllPlans } from '../config/pricing';
import CheckoutButton from '../components/CheckoutButton';

const PricingPage = () => {
  const [isYearly, setIsYearly] = useState(false);
  const { currency, formatPrice, isLoading } = useCurrency();

  // Product Schema for all three plans
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Local Business Heroes Platform",
    "description": "All-in-one CRM and marketing automation for local businesses. Capture leads, automate follow-up, reduce no-shows.",
    "brand": {
      "@type": "Brand",
      "name": "Local Business Heroes"
    },
    "offers": [
      {
        "@type": "Offer",
        "name": "Starter Plan",
        "price": "297",
        "priceCurrency": "GBP",
        "availability": "https://schema.org/InStock",
        "url": "https://localbusinessheroes.net/pricing"
      },
      {
        "@type": "Offer",
        "name": "Growth Plan",
        "price": "497",
        "priceCurrency": "GBP",
        "availability": "https://schema.org/InStock",
        "url": "https://localbusinessheroes.net/pricing"
      },
      {
        "@type": "Offer",
        "name": "Domination Plan",
        "price": "997",
        "priceCurrency": "GBP",
        "availability": "https://schema.org/InStock",
        "url": "https://localbusinessheroes.net/pricing"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200",
      "bestRating": "5"
    }
  };

  // FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How much does Local Business Heroes cost?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month for the Starter plan, £497/month for Growth, and £997/month for Domination. All plans include a 14-day trial for just £5."
        }
      },
      {
        "@type": "Question",
        "name": "Can I cancel anytime?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, all plans are no-contract and you can cancel anytime. No questions asked."
        }
      },
      {
        "@type": "Question",
        "name": "What's included in the £5 trial?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Full access to all features of your chosen plan for 14 days. After the trial, your plan continues at the standard monthly rate unless you cancel."
        }
      }
    ]
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Pricing", url: "/pricing" }
  ];
  
  // Get pricing plans with fallback to mock data
  let pricingPlans;
  try {
    pricingPlans = getAllPlans(currency) || mockPricingPlans;
  } catch (error) {
    console.warn('Error loading pricing plans, using fallback:', error);
    pricingPlans = mockPricingPlans;
  }
  
  // Ensure pricingPlans is always an array
  if (!Array.isArray(pricingPlans)) {
    pricingPlans = mockPricingPlans;
  }

  // Normalize pricing data structure to handle both new config and mock data
  const normalizedPlans = pricingPlans.map((plan, index) => ({
    id: plan.id || index + 1,
    name: plan.name,
    icon: plan.icon || (index === 0 ? '💼' : index === 1 ? '🚀' : '🏆'),
    amount: plan.amount || plan.price, // Support both amount and price properties
    popular: plan.popular || false,
    description: plan.description,
    features: plan.features || [],
    outcomes: plan.outcomes || []
  }));

  const getYearlyPrice = (monthlyPrice) => {
    return Math.floor(monthlyPrice * 10); // 2 months free
  };

  // Show loading state while currency context initializes
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-[#FF5A3D] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pricing...</p>
        </div>
      </div>
    );
  }

  const allFeatures = [
    { name: "Contacts", starter: "Up to 1,000", growth: "Up to 10,000", domination: "Unlimited" },
    { name: "Landing Pages", starter: "Unlimited", growth: "Unlimited", domination: "Unlimited" },
    { name: "Email Marketing", starter: "✓", growth: "✓ + Advanced", domination: "✓ + AI-Powered" },
    { name: "SMS Marketing", starter: "✓", growth: "✓ + Advanced", domination: "✓ + AI-Powered" },
    { name: "CRM Pipeline", starter: "Basic", growth: "Advanced", domination: "Enterprise" },
    { name: "Automation Workflows", starter: "5 workflows", growth: "Unlimited", domination: "Unlimited + AI" },
    { name: "Online Scheduler", starter: "✓", growth: "✓ + Advanced", domination: "✓ + Multi-location" },
    { name: "Review Management", starter: "✓", growth: "✓ + Monitoring", domination: "✓ + Reputation AI" },
    { name: "Social Media Scheduler", starter: "❌", growth: "✓", domination: "✓ + Multi-platform" },
    { name: "Analytics & Reporting", starter: "Basic", growth: "Advanced", domination: "Enterprise + Custom" },
    { name: "Integrations", starter: "10+", growth: "50+", domination: "Unlimited + Custom" },
    { name: "Support", starter: "Email", growth: "Phone + Email", domination: "Dedicated Manager" },
    { name: "Setup Assistance", starter: "❌", growth: "✓", domination: "✓ + Custom Training" },
    { name: "API Access", starter: "❌", growth: "❌", domination: "✓" },
    { name: "Multi-location Support", starter: "❌", growth: "❌", domination: "✓" }
  ];

  return (
    <>
      <SEO 
        title="Pricing & Plans | Local Business Heroes UK ⭐ Start £5 Trial"
        description="Simple, transparent pricing. Starter £297/mo, Growth £497/mo, Domination £997/mo. All plans include 14-day trial for £5. No contracts. Cancel anytime. Rated 4.9/5 by 1,200+ UK businesses."
        keywords="CRM pricing UK, marketing automation pricing, small business CRM cost, affordable CRM UK, business automation pricing, CRM plans UK, marketing software pricing"
        url="/pricing"
        schemaMarkup={[productSchema, faqSchema]}
        breadcrumbs={breadcrumbs}
      />
    <div className="min-h-screen bg-gray-50">
      {/* Trust Bar - Social Proof at Top */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-3 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-8 text-xs sm:text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              <span className="font-semibold">5,247+ businesses growing</span>
            </div>
            <div className="flex items-center space-x-2">
              <span>⭐⭐⭐⭐⭐</span>
              <span>4.9/5 from 1,200+ reviews</span>
            </div>
            <div className="flex items-center space-x-2">
              <span>🔒</span>
              <span>256-bit SSL Secure Checkout</span>
            </div>
            <div className="flex items-center space-x-2">
              <span>✅</span>
              <span>14-day money-back guarantee</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0E163B] via-[#1a2654] to-[#0E163B] text-white py-16 sm:py-20 px-4 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF5A3D]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          {/* Urgency Badge */}
          <div className="inline-flex items-center bg-red-500/20 border border-red-400/30 rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-6">
            <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
            <span className="text-xs sm:text-sm font-bold text-red-300">🔥 LIMITED TIME: Only {formatPrice(5)} to start (Usually {formatPrice(297)})</span>
          </div>
          
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Stop Losing 73% Of Your Leads.
            <br />
            <span className="text-[#FF5A3D]">Start Converting Them Instead.</span>
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-6 sm:mb-8 max-w-4xl mx-auto leading-relaxed">
            Join 5,247 UK businesses capturing 40% more leads and converting 60% more customers — 
            <strong className="text-white"> fully automated, zero extra effort.</strong>
          </p>

          {/* Key Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8 max-w-4xl mx-auto mb-8 sm:mb-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20">
              <div className="text-3xl sm:text-4xl font-bold text-[#FF5A3D] mb-2">14</div>
              <div className="text-xs sm:text-sm text-gray-300">Days Trial Period</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20">
              <div className="text-3xl sm:text-4xl font-bold text-[#FF5A3D]">{formatPrice(5)}</div>
              <div className="text-xs sm:text-sm text-gray-300">To Get Started</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20">
              <div className="text-3xl sm:text-4xl font-bold text-[#FF5A3D]">5 min</div>
              <div className="text-xs sm:text-sm text-gray-300">Setup Time</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20">
              <div className="text-3xl sm:text-4xl font-bold text-[#FF5A3D]">0%</div>
              <div className="text-xs sm:text-sm text-gray-300">Risk (Money Back)</div>
            </div>
          </div>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center space-x-3 sm:space-x-4">
            <span className={`text-sm sm:text-base font-semibold ${!isYearly ? 'text-white' : 'text-gray-400'}`}>Monthly</span>
            <button
              onClick={() => setIsYearly(!isYearly)}
              className="relative inline-flex h-6 w-11 items-center rounded-full bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF5A3D] focus:ring-offset-2"
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isYearly ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
            <span className={`text-sm sm:text-base font-semibold ${isYearly ? 'text-white' : 'text-gray-400'}`}>Yearly</span>
            {isYearly && (
              <Badge className="bg-[#FF5A3D] text-white ml-2 animate-bounce">
                💰 Save 2 months!
              </Badge>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section id="plans" className="py-16 sm:py-24 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          {/* Social Proof Above Cards */}
          <div className="text-center mb-16">
            <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-8 mb-8">
              <div className="flex items-center space-x-2 text-gray-600">
                <CheckCircle className="text-green-500" size={20} />
                <span className="text-sm sm:text-base">Just {formatPrice(5)} to start</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <CheckCircle className="text-green-500" size={20} />
                <span className="text-sm sm:text-base">Cancel anytime</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <CheckCircle className="text-green-500" size={20} />
                <span className="text-sm sm:text-base">Setup in 5 minutes</span>
              </div>
            </div>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              <strong className="text-gray-900">Trusted by 5,247+ UK businesses.</strong> Join them and start growing today.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10 max-w-6xl mx-auto mt-12 pt-8">
            {normalizedPlans.map((plan, index) => (
              <Card key={plan.id} className={`relative ${plan.popular ? 'ring-4 ring-[#FF5A3D] shadow-2xl z-10 transform md:scale-105' : 'shadow-lg'} hover:shadow-2xl transition-all duration-300 bg-white overflow-visible`}>
                {plan.popular && (
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-full flex justify-center">
                    <Badge className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] text-white px-6 py-3 text-sm font-bold shadow-lg whitespace-nowrap">
                      ⭐ MOST POPULAR - 67% Choose This
                    </Badge>
                  </div>
                )}
                {index === 0 && (
                  <div className="absolute -top-5 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-blue-600 text-white px-4 py-2 text-xs whitespace-nowrap">
                      🚀 Best for Beginners
                    </Badge>
                  </div>
                )}
                {index === 2 && (
                  <div className="absolute -top-5 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-purple-600 text-white px-4 py-2 text-xs whitespace-nowrap">
                      👑 Premium Experience
                    </Badge>
                  </div>
                )}
                
                <CardContent className="pt-14 sm:pt-16 pb-10 px-6 sm:px-8">
                  <div className="text-center mb-10">
                    <div className="text-5xl sm:text-6xl mb-4">{plan.icon}</div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-[#0E163B] mb-3">{plan.name}</h3>
                    <p className="text-gray-600 text-sm sm:text-base mb-8">{plan.description}</p>
                    
                    {/* Price Display */}
                    <div className="mb-8">
                      {isYearly && (
                        <div className="text-sm text-gray-500 line-through mb-2">
                          Was {formatPrice(plan.amount * 12)}/year
                        </div>
                      )}
                      <div className="flex items-baseline justify-center mb-2">
                        <span className="text-5xl sm:text-6xl font-bold text-[#0E163B]">
                          {formatPrice(isYearly ? getYearlyPrice(plan.amount) : plan.amount)}
                        </span>
                        <span className="text-gray-600 ml-2 text-lg">
                          {isYearly ? '/year' : '/month'}
                        </span>
                      </div>
                      {isYearly && (
                        <div className="text-sm text-[#FF5A3D] font-bold mt-3 bg-red-50 inline-block px-4 py-2 rounded-full">
                          💰 Save {formatPrice(plan.amount * 2)}/year
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-3">
                        Billed {isYearly ? 'annually' : 'monthly'} after trial
                      </div>
                    </div>
                  </div>
                  
                  {/* CTA Button */}
                  <CheckoutButton
                    plan={plan.name.includes('Starter') ? 'STARTER' : plan.name.includes('Growth') ? 'GROWTH' : 'DOMINATION'}
                    className={`w-full mb-6 ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white shadow-lg shadow-[#FF5A3D]/30'
                        : 'bg-[#0E163B] hover:bg-[#1a2654] text-white'
                    } font-bold py-5 sm:py-6 text-base sm:text-lg rounded-xl transition-all duration-300 transform hover:scale-105`}
                  >
                    Start 14-Day Trial - Just {formatPrice(5)}
                  </CheckoutButton>

                  <div className="text-center text-xs text-gray-500 mb-8">
                    ✅ 14-day full access • ✅ No contracts • ✅ Cancel anytime
                  </div>

                  {/* Expected Results */}
                  {plan.outcomes && plan.outcomes.length > 0 && (
                    <div className="mb-8 p-5 bg-green-50 border-2 border-green-200 rounded-xl">
                      <h5 className="font-bold text-green-800 mb-4 flex items-center justify-center text-base">
                        <span className="mr-2">📈</span> Expected Results:
                      </h5>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {plan.outcomes.map((outcome, idx) => (
                          <Badge key={idx} className="bg-green-600 text-white text-xs font-semibold px-3 py-1">
                            {outcome}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Features List */}
                  <div className="text-left space-y-4 pt-8 border-t-2 border-gray-200">
                    <h4 className="font-bold text-[#0E163B] text-center mb-6 text-lg">Everything Included:</h4>
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start space-x-3 text-sm">
                        <CheckCircle size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Trust Badges Below Cards */}
          <div className="text-center mt-16">
            <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-12 mb-8">
              <div className="text-center">
                <div className="text-3xl mb-2">🔒</div>
                <div className="text-xs text-gray-600 max-w-[100px]">Bank-Level Security</div>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">✅</div>
                <div className="text-xs text-gray-600 max-w-[100px]">14-Day Money Back</div>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">⚡</div>
                <div className="text-xs text-gray-600 max-w-[100px]">Instant Activation</div>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">📞</div>
                <div className="text-xs text-gray-600 max-w-[100px]">UK Support Team</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Social Proof */}
      <section className="py-16 sm:py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-green-600 text-white px-4 py-2 text-sm mb-4">
              ⭐ Real Results from Real Businesses
            </Badge>
            <h2 className="text-3xl sm:text-5xl font-bold text-[#0E163B] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Why 5,247 Businesses Trust Us
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it. See what business owners just like you are saying.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                name: "David M.",
                business: "Plumbing Services",
                location: "Manchester",
                result: "3x more booked jobs",
                quote: "We went from 12 jobs a month to 38. The automated booking system works while I'm on calls. Best decision we've made.",
                time: "2 months",
                image: "https://images.unsplash.com/photo-1660074127797-1c429fbb8cd6?w=400&h=400&fit=crop&crop=faces"
              },
              {
                name: "Sarah K.",
                business: "Hair Salon Owner",
                location: "London",
                result: "£8,400 extra revenue",
                quote: "The automated reminders cut no-shows by 80%. We're now fully booked weeks in advance. Couldn't run the salon without it.",
                time: "4 months",
                image: "https://images.unsplash.com/photo-1753351052363-53ce102830eb?w=400&h=400&fit=crop&crop=faces"
              },
              {
                name: "Tom R.",
                business: "Electrician",
                location: "Birmingham",
                result: "60% more leads",
                quote: "I was missing 40% of calls. Now every enquiry gets captured automatically. My phone rings non-stop with real customers.",
                time: "3 weeks",
                image: "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=400&h=400&fit=crop&crop=faces"
              }
            ].map((testimonial, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#FF5A3D]/30 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-gray-50">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <img 
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover mr-3 border-2 border-[#FF5A3D]"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF5A3D] to-[#FF8C42] flex items-center justify-center text-white font-bold text-xl mr-3" style={{display: 'none'}}>
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-xs text-gray-600">{testimonial.business} • {testimonial.location}</div>
                    </div>
                  </div>
                  
                  <div className="mb-3 flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <span key={i}>⭐</span>
                    ))}
                  </div>
                  
                  <div className="bg-green-100 text-green-800 font-bold text-sm px-3 py-2 rounded-lg inline-block mb-4">
                    📈 {testimonial.result}
                  </div>
                  
                  <p className="text-gray-700 text-sm leading-relaxed mb-3">
                    "{testimonial.quote}"
                  </p>
                  
                  <div className="text-xs text-gray-500">
                    ✓ Verified Customer • Started {testimonial.time} ago
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 text-lg mb-4">
              <strong className="text-gray-900">Join 5,247 businesses</strong> already growing with Local Business Heroes
            </p>
            <div className="flex justify-center items-center space-x-2">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF5A3D] to-[#FF8C42] border-2 border-white flex items-center justify-center text-white font-bold">
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
              </div>
              <span className="text-sm text-gray-600">+5,242 more happy customers</span>
            </div>
          </div>
        </div>
      </section>

      {/* Complimentary Credits Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#FF5A3D] text-white px-4 py-2 text-sm mb-4">
              💳 Complimentary Credits Included
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Free Credits with Every Subscription
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              All plans include complimentary credits upon purchase. Use them for premium features without worrying about extra charges.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Credits Explanation Card */}
            <Card className="border-2 border-[#FF5A3D]/20 hover:border-[#FF5A3D]/40 transition-all">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-[#FF5A3D]/10 p-3 rounded-lg">
                    <svg className="w-8 h-8 text-[#FF5A3D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#0E163B] mb-3">How Credits Work</h3>
                    <p className="text-gray-600 mb-4">
                      Every subscription plan includes free credits to use for premium features. Credits automatically replenish monthly with your subscription.
                    </p>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm text-green-800">
                        <strong>💡 Pro Tip:</strong> Unused credits roll over to the next month, so you never lose them!
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Plan Credits Card */}
            <Card className="border-2 border-[#FF5A3D]/20 hover:border-[#FF5A3D]/40 transition-all">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-green-500/10 p-3 rounded-lg">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#0E163B] mb-3">Credits by Plan</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="font-semibold text-gray-700">💼 Starter Plan (10 users)</span>
                        <Badge className="bg-[#FF5A3D] text-white">{formatPrice(25)} credits/month</Badge>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="font-semibold text-gray-700">🚀 Growth Plan (25 users)</span>
                        <Badge className="bg-[#FF5A3D] text-white">{formatPrice(50)} credits/month</Badge>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="font-semibold text-gray-700">🏆 Domination Plan (50 users)</span>
                        <Badge className="bg-[#FF5A3D] text-white">{formatPrice(100)} credits/month</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Premium Features Using Credits */}
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold text-[#0E163B] mb-4">
                Premium Features That Use Credits
              </h3>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Your complimentary credits can be used for these advanced features. Only pay for what you use beyond your included credits.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: '📞', name: 'Phone System', description: 'Cloud-based business phone' },
                { icon: '✉️', name: 'Email', description: 'Transactional email sending' },
                { icon: '⚡', name: 'Workflow Premium Features', description: 'Advanced automation actions' },
                { icon: '✅', name: 'LC Email Verification', description: 'Real-time email validation' },
                { icon: '🤖', name: 'Content AI', description: 'AI-powered content generation' },
                { icon: '🧠', name: 'External AI Models', description: 'GPT-4, Claude & more' },
                { icon: '💬', name: 'Conversation & Voice AI', description: 'AI chatbots & voice calls' },
                { icon: '📱', name: 'WhatsApp Usage', description: 'WhatsApp messaging integration' },
                { icon: '⭐', name: 'Reviews AI', description: 'AI review responses' },
                { icon: '🌐', name: 'Domain Purchase', description: 'Buy domains directly' },
                { icon: '🎯', name: 'Funnel AI', description: 'AI-optimized funnels' },
                { icon: '🔧', name: 'Agent Studio', description: 'Custom AI agent creation' }
              ].map((feature, index) => (
                <div key={index} className="flex items-start space-x-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-[#FF5A3D] hover:shadow-md transition-all">
                  <span className="text-2xl flex-shrink-0">{feature.icon}</span>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{feature.name}</h4>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Need More Credits?</h4>
                  <p className="text-blue-800 text-sm">
                    If you exceed your monthly credits, you'll only be charged for the additional usage at competitive rates. 
                    You can also upgrade to a higher plan for more included credits. View your credit usage anytime in your dashboard.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Compare Plans & Features
            </h2>
            <p className="text-xl text-gray-600">
              See exactly what's included in each plan
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0E163B] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Feature</th>
                    <th className="px-6 py-4 text-center font-semibold">Hero Starter</th>
                    <th className="px-6 py-4 text-center font-semibold bg-[#FF5A3D]/20">Hero Growth</th>
                    <th className="px-6 py-4 text-center font-semibold">Hero Domination</th>
                  </tr>
                </thead>
                <tbody>
                  {allFeatures.map((feature, index) => (
                    <tr key={index} className={`border-b ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                      <td className="px-6 py-4 font-medium text-gray-900">{feature.name}</td>
                      <td className="px-6 py-4 text-center text-sm">
                        {feature.starter === '❌' ? (
                          <X size={16} className="text-red-500 mx-auto" />
                        ) : feature.starter === '✓' ? (
                          <CheckCircle size={16} className="text-green-500 mx-auto" />
                        ) : (
                          <span className="text-gray-700">{feature.starter}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-sm bg-[#FF5A3D]/5">
                        {feature.growth === '❌' ? (
                          <X size={16} className="text-red-500 mx-auto" />
                        ) : feature.growth === '✓' ? (
                          <CheckCircle size={16} className="text-green-500 mx-auto" />
                        ) : (
                          <span className="text-gray-700 font-medium">{feature.growth}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-sm">
                        {feature.domination === '❌' ? (
                          <X size={16} className="text-red-500 mx-auto" />
                        ) : feature.domination === '✓' ? (
                          <CheckCircle size={16} className="text-green-500 mx-auto" />
                        ) : (
                          <span className="text-gray-700">{feature.domination}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-blue-600 text-white px-4 py-2 text-sm mb-4">
              ❓ Got Questions?
            </Badge>
            <h2 className="text-3xl sm:text-5xl font-bold text-[#0E163B] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-lg sm:text-xl text-gray-600">
              Everything you need to know — answered honestly
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4 mb-12">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`} className="border-2 border-gray-200 rounded-xl px-6 hover:border-[#FF5A3D]/30 transition-colors">
                <AccordionTrigger className="text-left font-bold text-[#0E163B] hover:text-[#FF5A3D] py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pt-2 pb-6 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-[#0E163B] mb-3">
              Still have questions?
            </h3>
            <p className="text-gray-600 mb-6">
              Our UK-based support team typically responds within 2 hours during business hours
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-bold">
                <a href="#plans">
                  💳 Start Your {formatPrice(5)} Trial Now
                </a>
              </Button>
              <Button asChild variant="outline" className="border-2 border-gray-300 hover:border-[#FF5A3D] hover:text-[#FF5A3D]">
                <a href="/contact">📞 Chat with Our Team</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final Urgency CTA */}
      <section className="py-16 sm:py-20 px-4 bg-gradient-to-br from-[#FF5A3D] via-[#FF6B52] to-[#FF8C42] text-white relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-48 translate-y-48"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-block bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-6 py-2 mb-6">
            <span className="font-bold">⏰ Limited Time Offer</span>
          </div>
          
          <h2 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Stop Losing Leads.<br />
            Start Growing Today.
          </h2>
          
          <p className="text-lg sm:text-2xl mb-8 text-white/90 max-w-3xl mx-auto leading-relaxed">
            Join <strong>5,247+ businesses</strong> already growing with Local Business Heroes.<br />
            Your competitors are already ahead. Don't get left behind.
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-10 max-w-2xl mx-auto">
            <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl p-6">
              <div className="text-4xl font-bold mb-2">{formatPrice(5)}</div>
              <div className="text-sm text-white/80">To Start Trial</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl p-6">
              <div className="text-4xl font-bold mb-2">14</div>
              <div className="text-sm text-white/80">Days Full Access</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl p-6">
              <div className="text-4xl font-bold mb-2">0%</div>
              <div className="text-sm text-white/80">Risk (Money Back)</div>
            </div>
          </div>

          <div className="space-y-4">
            <Button asChild size="lg" className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-bold text-lg px-10 py-7 rounded-xl shadow-2xl transform hover:scale-105 transition-all">
              <a href="#plans">
                🚀 Start My 14-Day Trial - {formatPrice(5)}
              </a>
            </Button>
            
            <div className="text-sm text-white/90">
              ✓ Full access in 5 minutes  •  ✓ Just {formatPrice(5)} to start  •  ✓ Cancel anytime
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/20">
            <p className="text-sm text-white/70 mb-2">🔒 Your data is safe. We use bank-level 256-bit SSL encryption.</p>
            <div className="flex flex-wrap justify-center gap-6 text-xs text-white/60">
              <span>✓ GDPR Compliant</span>
              <span>✓ ISO 27001 Certified</span>
              <span>✓ PCI DSS Level 1</span>
              <span>✓ SOC 2 Type II</span>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default PricingPage;